import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    applyUserAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    buildUserAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
    getUserAsyncFilterLabelFieldKey,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { submitReadyToSendReportFilterData, deleteReadyToSendReportItem } from "model/ReadyToSendReportModel";
import Swal from "sweetalert2";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";


const TABLE_COLUMNS = [
    { Header: "Id", accessor: "id" },
    { Header: "Candidate", accessor: "candidate" },
    { Header: "Client", accessor: "client" },
    { Header: "Create By", accessor: "createBy" },
    { Header: "Create Date", accessor: "createDate" },
    { Header: "Status", accessor: "status" },
];

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data &&
        typeof responseData.data === "object" &&
        !Array.isArray(responseData.data)
            ? responseData.data
            : responseData;

    let rows = [];
    if (Array.isArray(nestedData?.rows)) rows = nestedData.rows;
    else if (Array.isArray(responseData?.rows)) rows = responseData.rows;
    else if (Array.isArray(nestedData)) rows = nestedData;
    else if (Array.isArray(responseData?.data)) rows = responseData.data;
    else if (Array.isArray(responseData?.records)) rows = responseData.records;

    const pagination = {
        pageNumber:
            Number(nestedData?.pageNumber ?? responseData?.pageNumber) || 1,
        pageSize: Number(nestedData?.pageSize ?? responseData?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(nestedData?.totalPages ?? responseData?.totalPages) || 0,
        totalRecords:
            Number(nestedData?.totalRecords ?? responseData?.totalRecords) || 0,
    };

    return { rows, pagination };
};

const formatCreateDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("hh:mm A DD-MM-YYYY") : String(value);
};

const mapRowsFromApi = (rows = []) =>
    (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
        ...item,
        id: item?.id ?? item?.serialNumber ?? item?.sN ?? index + 1,
        candidate:
            item?.candidateFullName ??
            item?.candidateName ??
            item?.candidate ??
            item?.name ??
            item?.fullName ??
            "",
        client: item?.clientName ?? item?.client ?? "",
        createBy:
            item?.createdByName ??
            item?.createByName ??
            item?.createBy ??
            item?.createdBy ??
            item?.userName ??
            "",
        createDate: formatCreateDate(
            item?.createdOn ??
                item?.createDateIso ??
                item?.createDate ??
                item?.createdAt ??
                item?.createAt
        ),
        status: item?.statusId ?? item?.status ?? item?.statusTitle ?? item?.statusLabel ?? "",
        canDelete: Boolean(item?.canDelete),
    }));

const ReadyToSendReport = () => {
    const [filterForm, setFilterForm] = useState({
        nameContains: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
        userId: "",
        [getUserAsyncFilterLabelFieldKey()]: "",
        fromDate: "",
        toDate: "",
    });
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [requestPayload, setRequestPayload] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        nameContains: null,
        clientId: null,
        userId: null,
        dateFrom: null,
        dateTo: null,
    });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });

    const filteredData = useMemo(() => reportData, [reportData]);
    const hasDeletableRows = useMemo(
        () => filteredData.some((row) => row?.canDelete),
        [filteredData]
    );

    const buildFilterPayload = (overrides = {}) => ({
        pageNumber: overrides.pageNumber ?? requestPayload.pageNumber ?? 1,
        pageSize: overrides.pageSize ?? requestPayload.pageSize ?? DEFAULT_PAGE_SIZE,
        nameContains: strOrNull(
            overrides.nameContains ?? filterForm?.nameContains
        ),
        clientId: strOrNull(overrides.clientId ?? filterForm?.clientId),
        userId: strOrNull(overrides.userId ?? filterForm?.userId),
        dateFrom:
            overrides.dateFrom !== undefined
                ? overrides.dateFrom
                : normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo:
            overrides.dateTo !== undefined
                ? overrides.dateTo
                : normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getReadyToSendReportList = async (overrides = {}) => {
        setLoading(true);
        try {
            const payload = buildFilterPayload(overrides);
            const response = await submitReadyToSendReportFilterData(payload);
            const { rows, pagination } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setPaginationMeta({
                    pageNumber: Number(payload.pageNumber) || 1,
                    pageSize: Number(payload.pageSize) || DEFAULT_PAGE_SIZE,
                    totalPages: 0,
                    totalRecords: 0,
                });
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            setReportData(mapRowsFromApi(rows));
            setPaginationMeta({
                pageNumber:
                    pagination.pageNumber || Number(payload.pageNumber) || 1,
                pageSize: pagination.pageSize || Number(payload.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: pagination.totalPages || 0,
                totalRecords: pagination.totalRecords || 0,
            });
            setRequestPayload({
                pageNumber: Number(payload.pageNumber) || 1,
                pageSize: Number(payload.pageSize) || DEFAULT_PAGE_SIZE,
                nameContains: payload.nameContains,
                clientId: payload.clientId,
                userId: payload.userId,
                dateFrom: payload.dateFrom,
                dateTo: payload.dateTo,
            });
        } catch (error) {
            toast.error(error?.message || "Unable to load ready to send report");
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = async (page) => {
        await getReadyToSendReportList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getReadyToSendReportList({ pageNumber: 1, pageSize: rows });
    };

    const handleSearch = () => {
        getReadyToSendReportList({ pageNumber: 1 });
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleDelete = async (row = {}) => {
        if (!row?.canDelete) return;

        const id = String(row?.id ?? "").trim();
        if (!id) {
            toast.error("Record ID is missing");
            return;
        }

        const swalResult = await Swal.fire({
            title: "Delete ready to send record?",
            text: "Are you sure you want to delete this record?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!swalResult?.isConfirmed) return;

        setLoading(true);
        try {
            const res = await deleteReadyToSendReportItem(id);
            if (res?.success !== false && (res?.success || res?.isSuccess || res?.data != null)) {
                toast.success(res?.message || "Deleted successfully");
                await getReadyToSendReportList();
                return;
            }
            toast.error(res?.message || "Unable to delete");
        } finally {
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Ready To Send Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">Search</span>
                                        <Input
                                            type="text"
                                            name="nameContains"
                                            placeholder="Search by Email/Com"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.nameContains || ""}
                                            onChange={handleFilterFieldChange}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter") {
                                                    handleSearch();
                                                }
                                            }}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`ready-to-send-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportUnrestricted"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">User</span>
                                        <AsyncUserSelect
                                            key={`ready-to-send-user-${filterForm?.userId || ""}`}
                                            pageName="ReadyToSendReport"
                                            value={filterForm?.userId || ""}
                                            initialOption={buildUserAsyncFilterInitialOption(
                                                filterForm?.userId,
                                                filterForm[getUserAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyUserAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select User"
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">From date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span className="manage-user-filter-label">To date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={LEGACY_FILTER_INPUT_CLASS}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={handleSearch}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <CommonTable
                                    columns={TABLE_COLUMNS}
                                    data={filteredData}
                                    isActionRequired={hasDeletableRows}
                                    onDelete={handleDelete}
                                    pageName="ReadyToSendReport"
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

ReadyToSendReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ReadyToSendReport);
